/**
 * A kid's tap is not a mouse click (D-161).
 *
 * A browser only turns a touch into a `click` if the finger stays inside a small
 * slop box — about 15 CSS pixels in Chromium, and the same order in Safari. Past
 * that the gesture is a scroll: the browser sends `pointercancel`, no `pointerup`
 * and no `click`, and the button the finger was on does nothing at all. That
 * threshold is a fair guess about an adult with a mouse-sized fingertip. It is
 * the wrong guess about a 2- and a 4-year-old, who plant a whole finger pad on
 * Fight! and slide it a centimetre while they press — which is why the big
 * forward buttons "sometimes need two or three taps". Measured on the real
 * screens, in Chromium at the iPad size: a 12px slide clicks, a 16px slide is
 * silent.
 *
 * So the app decides for itself what a tap is. A touch that starts on a button,
 * ends on the same button, slides no more than SLOP and scrolls nothing is a
 * tap, and if the browser did not call it one we click the button ourselves.
 *
 * What keeps this from firing twice:
 *   - We only stand in when the browser's own click did not arrive. A tap the
 *     browser accepts is dispatched within a frame of `touchend` — the 300ms
 *     double-tap wait is gone, because `touch-action: manipulation` is on every
 *     button in global.css — so a short look is enough to know.
 *   - After standing in we swallow a real click on that button for a moment, in
 *     case a browser we have not measured is slower than that look.
 *   - A touch that scrolled the page further than SLOP is not a tap. That is what
 *     stops a drag down the roster grid from picking the card it began on.
 *     A scroll *inside* SLOP is only the finger's own drift carrying the page
 *     with it (D-183): on a phone every screen scrolls, and a downward drift on
 *     Next moves it a few pixels before the finger lifts. That used to
 *     disqualify the tap outright, which left the phone with the very bug this
 *     file exists for.
 *   - A touch that lands while the page is still coasting from a flick is the
 *     kid stopping the scroll, and every browser treats it that way: no click,
 *     and we do not stand in for one.
 *
 * Deliberately narrow: real `<button>` elements only. The battle's tap-anywhere
 * scene and the fact pages' swipe are gestures with their own rules, and this
 * has no business second-guessing them.
 */

/** How far a finger may slide and still mean "I pressed this". */
const SLOP = 30
/** Longer than this is a hold — the top bar's age chip, for one — not a tap. */
const MAX_MS = 1200
/** How long to wait for the browser's own click before standing in. */
const CLICK_WAIT_MS = 60
/** How long a stood-in click suppresses a late real one on the same button. */
const SWALLOW_MS = 350
/** A scroll this recent when the finger lands means the page was still coasting. */
const COASTING_MS = 100

let installed = false

/**
 * Where everything that could carry this button has scrolled to: each ancestor,
 * and the page itself. Compared at touchend, it says how far the finger dragged
 * the page — not merely whether a scroll event fired.
 */
function scrollState(el) {
  const at = []
  for (let n = el.parentElement; n; n = n.parentElement) at.push(n, n.scrollLeft, n.scrollTop)
  at.push(null, window.scrollX, window.scrollY)
  return at
}

/** The furthest any of them moved since `scrollState` was taken. */
function scrolledBy(at) {
  let most = 0
  for (let k = 0; k < at.length; k += 3) {
    const n = at[k]
    const x = n ? n.scrollLeft : window.scrollX
    const y = n ? n.scrollTop : window.scrollY
    most = Math.max(most, Math.abs(x - at[k + 1]), Math.abs(y - at[k + 2]))
  }
  return most
}

/** Installed once by App.jsx. Returns the uninstall function. */
export function installTapRescue(target = typeof window !== 'undefined' ? window : null) {
  if (installed || !target) return () => {}
  installed = true

  let live = null // the touch in progress
  let watched = null // the finished touch we are waiting on a click for
  let swallow = null // { el, until }: this button's next real click is one of ours
  let lastScrollAt = 0

  /** The enabled button a touch landed on, if any. */
  const buttonAt = (node) => {
    if (!(node instanceof Element)) return null
    const el = node.closest('button')
    return el && !el.disabled && !el.closest('[data-no-tap-rescue]') ? el : null
  }

  const onScroll = () => { lastScrollAt = Date.now() }

  const onStart = (e) => {
    // Two fingers is a pinch, and a second finger part-way through abandons the first.
    if (e.touches.length !== 1) { live = null; return }
    const t = e.touches[0]
    const el = buttonAt(e.target)
    const coasting = Date.now() - lastScrollAt < COASTING_MS
    live = el && !coasting
      ? { el, id: t.identifier, x: t.clientX, y: t.clientY, at: Date.now(), scroll: scrollState(el), clicked: false }
      : null
  }

  /** Did this touch mean the button it started on? */
  const isTap = (g, t) => {
    if (Math.abs(t.clientX - g.x) > SLOP || Math.abs(t.clientY - g.y) > SLOP) return false
    if (Date.now() - g.at > MAX_MS) return false
    if (!g.el.isConnected || g.el.disabled) return false
    // The finger has to come up on the button it went down on, give or take the
    // same slop: a slide off the edge onto the next control is not this tap.
    const r = g.el.getBoundingClientRect()
    return t.clientX >= r.left - SLOP && t.clientX <= r.right + SLOP &&
           t.clientY >= r.top - SLOP && t.clientY <= r.bottom + SLOP
  }

  const onEnd = (e) => {
    const g = live
    live = null
    if (!g || e.touches.length || scrolledBy(g.scroll) > SLOP) return
    const t = [...e.changedTouches].find((x) => x.identifier === g.id)
    if (!t || !isTap(g, t)) return

    // The browser's own click, if it is coming, lands before this does.
    watched = g
    setTimeout(() => {
      if (watched === g) watched = null
      if (g.clicked || !g.el.isConnected || g.el.disabled) return
      swallow = { el: g.el, until: Date.now() + SWALLOW_MS }
      g.el.click()
    }, CLICK_WAIT_MS)
  }

  const onCancelTouch = () => { live = null }

  // Capture, so this runs before any screen acts on the click.
  const onClick = (e) => {
    const el = buttonAt(e.target)
    if (!el) return
    if (watched && el === watched.el) watched.clicked = true
    if (e.isTrusted && swallow && el === swallow.el && Date.now() < swallow.until) {
      swallow = null
      e.stopPropagation()
      e.preventDefault()
    }
  }

  const opts = { capture: true, passive: true }
  target.addEventListener('touchstart', onStart, opts)
  target.addEventListener('touchend', onEnd, opts)
  target.addEventListener('touchcancel', onCancelTouch, opts)
  target.addEventListener('scroll', onScroll, opts)
  target.addEventListener('click', onClick, { capture: true })

  return () => {
    target.removeEventListener('touchstart', onStart, { capture: true })
    target.removeEventListener('touchend', onEnd, { capture: true })
    target.removeEventListener('touchcancel', onCancelTouch, { capture: true })
    target.removeEventListener('scroll', onScroll, { capture: true })
    target.removeEventListener('click', onClick, { capture: true })
    installed = false
  }
}

export default { installTapRescue }
